import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase, daysBetween, getMeetingStatus, getInitials } from '../lib/supabase'
import { UserPlus, AlertCircle } from 'lucide-react'

const AVATAR_COLORS = ['avatar-blue','avatar-green','avatar-purple','avatar-orange','avatar-pink']
const getAvatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length]

export default function Overview({ activeFilter }) {
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('All')

  useEffect(() => {
    fetchStudents()
  }, [activeFilter])

  const fetchStudents = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .eq('status', activeFilter)
      .order('english_name')

    if (data) setStudents(data)
    setLoading(false)
  }

  const enriched = students.map(s => ({
    ...s,
    daysToMeeting: s.next_meeting_date ? daysBetween(s.next_meeting_date) : null,
    daysToRenewal: s.renewal_date ? daysBetween(s.renewal_date) : null,
  }))

  const overdue  = enriched.filter(s => s.daysToMeeting !== null && s.daysToMeeting < 0)
  const booked   = enriched.filter(s => s.meeting_booked)
  const renewSoon = enriched.filter(s => s.daysToRenewal !== null && s.daysToRenewal <= 30 && s.daysToRenewal >= 0)

  const filtered = statusFilter === 'All' ? enriched
    : statusFilter === 'Overdue' ? enriched.filter(s => s.daysToMeeting !== null && s.daysToMeeting < 0)
    : statusFilter === 'Booked' ? enriched.filter(s => s.meeting_booked)
    : enriched.filter(s => s.daysToMeeting !== null && s.daysToMeeting >= 0 && !s.meeting_booked)

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">
            {activeFilter === 'Active' ? 'Active students' : 'Past students'}
          </div>
          <div className="page-subtitle">
            {activeFilter === 'Active'
              ? `${students.length} students currently enrolled`
              : `${students.length} students — program completed or paused`}
          </div>
        </div>
        <div className="header-actions">
          <Link to="/add-student" className="btn btn-primary">
            <UserPlus size={14} />
            New student
          </Link>
        </div>
      </div>

      {activeFilter === 'Active' && (
        <div className="page-content" style={{paddingBottom: 0}}>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Enrolled</div>
              <div className="stat-value">{students.length}</div>
              <div className="stat-sub">active students</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Meetings overdue</div>
              <div className={`stat-value ${overdue.length > 0 ? 'red' : 'green'}`}>{overdue.length}</div>
              <div className="stat-sub">need follow-up</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Meetings booked</div>
              <div className="stat-value green">{booked.length}</div>
              <div className="stat-sub">this cycle</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Renewals soon</div>
              <div className={`stat-value ${renewSoon.length > 0 ? 'yellow' : 'green'}`}>{renewSoon.length}</div>
              <div className="stat-sub">within 30 days</div>
            </div>
          </div>
        </div>
      )}

      <div className="filter-bar">
        {['All', 'Overdue', 'Booked', 'On track'].map(f => (
          <button
            key={f}
            className={`filter-tag ${statusFilter === f ? 'active-tag' : ''}`}
            onClick={() => setStatusFilter(f)}
          >{f}</button>
        ))}
      </div>

      <div className="page-content">
        {loading ? (
          <div className="loading">Loading students...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={28} />
            <p>No {activeFilter.toLowerCase()} students found</p>
            {activeFilter === 'Active' && (
              <Link to="/add-student" className="btn btn-primary" style={{marginTop: 8}}>
                Add first student
              </Link>
            )}
          </div>
        ) : (
          <div className="students-grid">
            {filtered.map(student => {
              const meetingStatus = getMeetingStatus(student.daysToMeeting)
              const badgeClass = meetingStatus.color === 'red' ? 'badge-red'
                : meetingStatus.color === 'yellow' ? 'badge-yellow'
                : meetingStatus.color === 'green' ? 'badge-green' : 'badge-gray'

              return (
                <Link
                  key={student.id}
                  to={`/student/${student.id}`}
                  className={`student-card ${activeFilter === 'Inactive' ? 'inactive-card' : ''}`}
                >
                  <div className="sc-top">
                    <div className={`avatar ${getAvatarColor(student.english_name)}`}>
                      {getInitials(student.english_name)}
                    </div>
                    <div>
                      <div className="sc-name">{student.english_name}</div>
                      <div className="sc-meta">
                        {student.current_school || '—'} · Grade {student.grade_when_joined || '?'}
                      </div>
                    </div>
                  </div>
                  <div className="sc-badges">
                    <span className={`badge ${badgeClass}`}>{meetingStatus.label}</span>
                    {student.meeting_booked && <span className="badge badge-green">Booked ✓</span>}
                    {student.mbti_type && <span className="badge badge-blue">{student.mbti_type}</span>}
                    {activeFilter === 'Inactive' && <span className="badge badge-gray">Inactive</span>}
                    {!student.email && <span className="badge badge-yellow">No email</span>}
                  </div>
                  <div className="progress-list">
                    <div className="progress-item">
                      <span className="progress-label">Profile</span>
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{width: student.english_name && student.email ? '100%' : '50%'}}/>
                      </div>
                      <span className="progress-pct">{student.english_name && student.email ? '100' : '50'}%</span>
                    </div>
                    <div className="progress-item">
                      <span className="progress-label">MBTI</span>
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{width: student.mbti_type ? '100%' : '0%'}}/>
                      </div>
                      <span className="progress-pct">{student.mbti_type ? '100' : '0'}%</span>
                    </div>
                    <div className="progress-item">
                      <span className="progress-label">Renewal</span>
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{
                          width: student.daysToRenewal !== null
                            ? `${Math.min(100, Math.max(0, ((365 - student.daysToRenewal) / 365) * 100))}%`
                            : '0%',
                          background: student.daysToRenewal !== null && student.daysToRenewal <= 30 ? 'var(--yellow)' : 'var(--accent)'
                        }}/>
                      </div>
                      <span className="progress-pct">
                        {student.daysToRenewal !== null ? `${student.daysToRenewal}d` : '—'}
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
